import React, { useState } from "react"
import { XMarkIcon, Bars3Icon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Tooltip from "../../Tooltip"
import { url, filename } from "../url"
import { useFieldEditorController } from "../controllers/field-file-editor-controller"
import { useFieldDragController } from "../controllers/field-drag-controller"
import AudioPreview from "aldoo-ra/CMS/preview/audio-preview"
import MediaLibrary from "aldoo-ra/MediaLibrary"

export default function AudioEditor({
  onChange,
  value,
  field,
  showInfo = true,
  readOnly = false,
  parentObject,
}) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [currentPreviewAudio, setCurrentPreviewAudio] = useState(null)
  const [currentPreviewFileName, setCurrentPreviewFileName] = useState(null)
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)
  const [selectedAudioIndex, setSelectedAudioIndex] = useState(null)

  const {
    localValue,
    setLocalValue,
    handleChange,
    onRemoveItem,
    errors,
    setErrors,
  } = useFieldEditorController({
    value,
    field,
    onChange,
    category: "audio",
  })

  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, setLocalValue)

  const openPreview = (audioFile, name, index = null) => {
    setCurrentPreviewAudio(audioFile)
    setCurrentPreviewFileName(name)
    if (index !== null) setSelectedAudioIndex(index)
    setIsPreviewOpen(true)
  }

  const closePreview = () => {
    setIsPreviewOpen(false)
    setCurrentPreviewAudio(null)
    setCurrentPreviewFileName(null)
  }

  const handleMediaLibraryOpen = (index = null) => {
    if (index !== null) setSelectedAudioIndex(index)
    setIsMediaLibraryOpen(true)
  }

  const handleFileSelect = (file) => {
    if (!file) return
    const filePath = file.path || file.url
    if (!filePath) return
    const newFile = { url: filePath }

    if (!field.multiple) {
      setLocalValue(newFile)
      onChange(newFile)
    } else if (selectedAudioIndex !== null && Array.isArray(localValue) && localValue[selectedAudioIndex]) {
      const updated = JSON.parse(JSON.stringify(localValue))
      updated[selectedAudioIndex] = newFile
      // Applied immediately: a deferred write replays a stale closure.
      setLocalValue(updated)
      onChange(updated)
    } else {
      let updated = []
      if (Array.isArray(localValue)) updated = [...localValue, newFile]
      else if (localValue?.url) updated = [localValue, newFile]
      else updated = [newFile]
      setLocalValue(updated)
      onChange(updated)
    }

    setSelectedAudioIndex(null)
    setIsMediaLibraryOpen(false)
  }

  const onMediaAudioSelect = (audio) => {
    if (!audio?.path) return
    const newFile = { url: audio.path }
    const indexToUse = audio.selectedIndex !== undefined ? audio.selectedIndex : selectedAudioIndex

    if (indexToUse !== null && Array.isArray(localValue) && localValue[indexToUse]) {
      const updated = [...localValue]
      updated[indexToUse] = newFile
      setLocalValue(updated)
      onChange(updated)
    } else if (!field.multiple) {
      setLocalValue(newFile)
      onChange(newFile)
    } else {
      let updated = []
      if (Array.isArray(localValue)) updated = [...localValue, newFile]
      else if (localValue?.url) updated = [localValue, newFile]
      else updated = [newFile]
      setLocalValue(updated)
      onChange(updated)
    }

    if (audio.fromPreview) {
      setCurrentPreviewAudio(url(audio.path))
      setCurrentPreviewFileName(filename(audio.path))
    } else {
      closePreview()
    }
  }

  const renderItem = (item, index) => {
    if (!item) return null
    if (item.url?.includes("blob:")) return null

    const name = filename(url(item.url))

    return (
      <Tooltip key={index} content={name} position="top">
        <div
          className="relative flex-none w-40 border border-admin_bordered rounded-md overflow-hidden group cursor-pointer"
          draggable={!readOnly && localValue?.length > 1}
          onDragStart={!readOnly && localValue?.length > 1 ? () => handleDragStart(index) : undefined}
          onDragOver={!readOnly && localValue?.length > 1 ? (e) => { e.preventDefault(); handleDragOver(index) } : undefined}
          onDragEnd={!readOnly && localValue?.length > 1 ? handleDragEnd : undefined}
          onClick={() => openPreview(url(item.url), name, index)}
        >
          {/* Audio card */}
          <div className="flex flex-col items-center justify-center gap-2 p-3 bg-admin_neutral_2 h-24">
            <svg className="w-8 h-8 text-admin_text_secondary" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6zm-2 16a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
            </svg>
            {/* Upload progress */}
            {item.progress > 0 && item.progress < 100 && (
              <div className="w-full bg-admin_neutral_3 rounded-full h-1">
                <div
                  className="bg-admin_primary h-1 rounded-full transition-all"
                  style={{ width: `${item.progress}%` }}
                />
              </div>
            )}
          </div>

          <div className="px-2 py-1.5 bg-admin_paper">
            <span className="text-xs text-admin_text truncate block w-full">{name}</span>
          </div>

          {!readOnly && (
            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                className="text-white bg-admin_primary rounded-full w-5 h-5 flex items-center justify-center hover:bg-admin_primary_dk"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMediaLibraryOpen(index) }}
                title="Replace audio"
              >
                <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                className="text-white bg-admin_error rounded-full w-5 h-5 flex items-center justify-center hover:bg-admin_error/90"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveItem(index) }}
                title="Remove audio"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </div>
          )}

          {!readOnly && localValue?.length > 1 && (
            <div className="absolute top-1 left-1 bg-admin_neutral_2 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-move">
              <Bars3Icon className="w-3 h-3 text-admin_text" />
            </div>
          )}
        </div>
      </Tooltip>
    )
  }

  return (
    <div className="audio-editor">
      <div className="flex flex-row justify-end items-center">
        {!readOnly && (
          <Button
            variant="admin_secondary"
            className="mb-4"
            onClick={() => handleMediaLibraryOpen(null)}
          >
            {field.multiple ? "Add Audio" : "Select Audio"}
          </Button>
        )}
      </div>

      {!readOnly && field.multiple && localValue?.length > 0 && (
        <div className="text-sm text-admin_text mb-2 flex flex-row items-center">
          <Button
            variant="admin_secondary"
            className="mr-5"
            onClick={() => setLocalValue(field.multiple ? [] : null)}
          >
            {field.multiple ? "Clear All" : "Clear"}
          </Button>
          <div className="text-sm text-admin_text">
            {localValue.length} {localValue.length === 1 ? "file" : "files"}
          </div>
        </div>
      )}

      <div className="flex gap-4 flex-wrap py-2">
        {Array.isArray(localValue)
          ? localValue?.map((item, index) => renderItem(item, index))
          : renderItem(localValue, 0)}
      </div>

      {errors.length > 0 && (
        <div className="text-error text-center w-full mb-4">
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}

      {isPreviewOpen && (
        <AudioPreview
          isOpen={isPreviewOpen}
          audioUrl={currentPreviewAudio}
          fileName={currentPreviewFileName}
          onClose={closePreview}
          onAudioSelect={onMediaAudioSelect}
          selectedAudioIndex={selectedAudioIndex}
        />
      )}

      <MediaLibrary
        isOpen={isMediaLibraryOpen}
        onClose={() => { setIsMediaLibraryOpen(false); setSelectedAudioIndex(null) }}
        onFileSelect={handleFileSelect}
        title={selectedAudioIndex !== null ? "Replace Audio" : field.multiple ? "Add Audio" : "Select Audio"}
        defaultFileType="audio"
      />
    </div>
  )
}
